import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Lock, Eye, EyeOff, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { API } from '../auth/AuthContext';

const strength = (pw) => {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 4);
};
const labels = ['Muito fraca', 'Fraca', 'Razoável', 'Forte', 'Muito forte'];
const colors = ['bg-rose-500', 'bg-rose-500', 'bg-amber-500', 'bg-emerald-500', 'bg-emerald-600'];

export default function ChangePasswordModal({ open, onClose, onSuccess }) {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState({ cur: false, next: false, confirm: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!open) {
      setCur(''); setNext(''); setConfirm('');
      setShow({ cur: false, next: false, confirm: false });
      setError(''); setOk(false); setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  const s = strength(next);
  const rules = [
    { ok: next.length >= 8, label: 'Mínimo 8 caracteres' },
    { ok: /[A-Za-z]/.test(next), label: 'Pelo menos 1 letra' },
    { ok: /\d/.test(next), label: 'Pelo menos 1 número' },
    { ok: next.length > 0 && next === confirm, label: 'As senhas coincidem' },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next !== confirm) { setError('As senhas não coincidem.'); return; }
    setBusy(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: cur, new_password: next },
        { withCredentials: true }
      );
      setOk(true);
      setTimeout(() => { onSuccess?.(); }, 1200);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Falha ao alterar a senha.');
    } finally {
      setBusy(false);
    }
  };

  const field = (key, value, setValue, placeholder, autoComplete) => (
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <input
        type={show[key] ? 'text' : 'password'}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required
        className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 focus:bg-white"
      />
      <button
        type="button"
        onClick={() => setShow((v) => ({ ...v, [key]: !v[key] }))}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100"
        aria-label={show[key] ? 'Ocultar senha' : 'Mostrar senha'}
      >
        {show[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-base font-bold text-slate-900">Alterar senha</h3>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        {ok ? (
          <div className="px-5 py-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-900">Senha alterada com sucesso!</p>
            <p className="text-xs text-slate-500 mt-1">Faça login novamente com a nova senha.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="px-5 py-4 space-y-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Senha atual</label>
              {field('cur', cur, setCur, 'Digite sua senha atual', 'current-password')}
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Nova senha</label>
              {field('next', next, setNext, 'Mínimo 8 caracteres', 'new-password')}
              {next.length > 0 && (
                <div className="mt-2">
                  <div className="grid grid-cols-4 gap-1">
                    {[0,1,2,3].map(i => (
                      <span key={i} className={`h-1 rounded-full ${i < s ? colors[s] : 'bg-slate-200'}`} />
                    ))}
                  </div>
                  <p className={`text-[10px] mt-1 font-semibold ${s >= 3 ? 'text-emerald-700' : s >= 2 ? 'text-amber-700' : 'text-rose-700'}`}>{labels[s]}</p>
                </div>
              )}
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Confirmar nova senha</label>
              {field('confirm', confirm, setConfirm, 'Repita a nova senha', 'new-password')}
            </div>

            <ul className="space-y-1">
              {rules.map((r, i) => (
                <li key={i} className="flex items-center gap-2 text-[11px]">
                  <span className={`w-1.5 h-1.5 rounded-full ${r.ok ? 'bg-emerald-500' : 'bg-slate-300'}`}></span>
                  <span className={r.ok ? 'text-emerald-700' : 'text-slate-500'}>{r.label}</span>
                </li>
              ))}
            </ul>

            {error && (
              <div className="flex items-start gap-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg">
                <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                <p className="text-[12px] text-rose-700">{error}</p>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <button type="button" onClick={onClose} className="flex-1 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-lg">Cancelar</button>
              <button
                type="submit"
                disabled={busy || !rules.every(r => r.ok)}
                className="flex-1 inline-flex items-center justify-center gap-2 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white text-sm font-semibold rounded-lg"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Alterar senha
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
